import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { motion } from 'framer-motion';
import { 
  ChartBar, Car, UsersThree, Star, Gauge, SignOut, CaretRight,
  Eye, Mouse, TrendUp, ArrowUpRight, Globe, Calendar,
  ShoppingCart, CheckCircle, XCircle, Clock, MapPin
} from '@phosphor-icons/react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';

function AdminSidebar() {
  const { logout } = useAuth();
  const location = useLocation();
  const links = [
    { label: 'Dashboard', path: '/admin/dashboard', icon: Gauge },
    { label: 'Analytics', path: '/admin/analytics', icon: TrendUp },
    { label: 'Réservations', path: '/admin/reservations', icon: Car },
    { label: 'Utilisateurs', path: '/admin/utilisateurs', icon: UsersThree },
    { label: 'Avis', path: '/admin/avis', icon: Star },
    { label: 'Véhicules', path: '/admin/vehicules', icon: ChartBar },
  ];
  return (
    <aside className="w-full lg:w-64 shrink-0" data-testid="admin-sidebar">
      <div className="bg-desti-surface border border-white/5 p-6">
        <p className="text-xs uppercase tracking-[0.2em] text-desti-gold mb-4">Administration</p>
        <nav className="space-y-1">
          {links.map(l => (
            <Link key={l.path} to={l.path}
              className={`flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${location.pathname === l.path ? 'text-desti-gold bg-desti-gold/5' : 'text-white/60 hover:text-white'}`}
              data-testid={`admin-nav-${l.path.split('/').pop()}`}>
              <l.icon size={18} weight="duotone" /> {l.label}
            </Link>
          ))}
          <div className="line-gold my-3" />
          <Link to="/espace-membre/dashboard" className="flex items-center gap-3 px-3 py-2.5 text-sm text-white/40 hover:text-white transition-colors">
            <CaretRight size={14} /> Espace membre
          </Link>
          <button onClick={logout} className="flex items-center gap-3 px-3 py-2.5 text-sm text-white/40 hover:text-white transition-colors w-full">
            <SignOut size={18} /> Déconnexion
          </button>
        </nav>
      </div>
    </aside>
  );
}

// Périodes disponibles
const periods = [
  { label: 'Aujourd\'hui', days: 1 },
  { label: '7 jours', days: 7 },
  { label: '30 jours', days: 30 },
  { label: '90 jours', days: 90 },
];

// Couleurs pour les graphiques
const COLORS = ['#D4AF37', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

// État initial vide (plus de mock)
const EMPTY_ANALYTICS = {
  uniqueVisitors: 0,
  pageViews: 0,
  sessions: 0,
  bounceRate: 0,
  avgSessionDuration: 0,
  conversionRate: 0,
  conversions: 0,
  trafficOverTime: [],
  conversionFunnel: [],
  trafficSources: [],
  topPages: [],
  recentEvents: [],
};

export default function AdminAnalytics() {
  const [period, setPeriod] = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState(EMPTY_ANALYTICS);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Auto-refresh toutes les 30 secondes
  useEffect(() => {
    fetchAnalytics();
    const interval = setInterval(fetchAnalytics, 30000);
    return () => clearInterval(interval);
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/admin/analytics?days=${period}`);
      // S'assurer que toutes les données nécessaires sont présentes
      const data = {
        ...EMPTY_ANALYTICS,
        ...response.data,
        // S'assurer que les tableaux sont toujours des tableaux
        trafficOverTime: response.data.trafficOverTime || [],
        conversionFunnel: response.data.conversionFunnel || [],
        trafficSources: response.data.trafficSources || [],
        topPages: response.data.topPages || [],
        recentEvents: response.data.recentEvents || [],
      };
      setAnalytics(data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Erreur chargement analytics:', error);
      setError(error.response?.data?.message || 'Erreur lors du chargement des données');
      // Garder les données précédentes ou vides
      setAnalytics(prev => prev || EMPTY_ANALYTICS);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0s';
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  };

  // Formater le taux de rebond
  const formatBounceRate = (rate) => {
    if (!rate && rate !== 0) return '0%';
    return `${rate}%`;
  };

  // Formater le taux de conversion
  const formatConversionRate = (rate) => {
    if (!rate && rate !== 0) return '0%';
    return `${rate}%`;
  };

  return (
    <div className="pt-20 min-h-screen bg-desti-black" data-testid="admin-analytics-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          <AdminSidebar />
          
          <div className="flex-1">
            {/* Header avec période */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
              <div>
                <h1 className="font-heading text-3xl text-white">Analytics</h1>
                {lastRefresh && (
                  <p className="text-xs text-desti-text mt-1">
                    Dernière mise à jour : {lastRefresh.toLocaleTimeString('fr-FR')}
                  </p>
                )}
              </div>
              <div className="flex gap-2 bg-desti-surface border border-white/5 rounded-lg p-1">
                {periods.map(p => (
                  <button
                    key={p.days}
                    onClick={() => setPeriod(p.days)}
                    className={`px-4 py-2 text-sm transition-colors rounded-md ${
                      period === p.days
                        ? 'bg-desti-gold text-desti-black font-medium'
                        : 'text-white/60 hover:text-white'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Message d'erreur */}
            {error && (
              <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <p className="text-red-400 text-sm">{error}</p>
                <button 
                  onClick={fetchAnalytics}
                  className="mt-2 text-sm text-desti-gold hover:text-desti-gold-hover transition-colors"
                >
                  Réessayer
                </button>
              </div>
            )}

            {loading && analytics.uniqueVisitors === 0 ? (
              <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-desti-gold"></div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Cartes KPI */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
                    className="bg-desti-surface border border-white/5 p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-desti-text uppercase tracking-wide">Visiteurs uniques</p>
                        <p className="text-3xl font-heading text-white mt-1">{analytics.uniqueVisitors || 0}</p>
                      </div>
                      <Eye size={32} className="text-desti-gold opacity-50" />
                    </div>
                  </motion.div>

                  <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
                    className="bg-desti-surface border border-white/5 p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-desti-text uppercase tracking-wide">Pages vues</p>
                        <p className="text-3xl font-heading text-white mt-1">{analytics.pageViews || 0}</p>
                        <p className="text-xs text-desti-text mt-1">{analytics.sessions || 0} sessions</p>
                      </div>
                      <Mouse size={32} className="text-desti-gold opacity-50" />
                    </div>
                  </motion.div>

                  <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                    className="bg-desti-surface border border-white/5 p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-desti-text uppercase tracking-wide">Taux de rebond</p>
                        <p className="text-3xl font-heading text-white mt-1">{formatBounceRate(analytics.bounceRate)}</p>
                        <p className="text-xs text-desti-text mt-1">{formatDuration(analytics.avgSessionDuration)} en moyenne</p>
                      </div>
                      <TrendUp size={32} className="text-desti-gold opacity-50" />
                    </div>
                  </motion.div>

                  <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                    className="bg-desti-surface border border-white/5 p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-desti-text uppercase tracking-wide">Taux de conversion</p>
                        <p className="text-3xl font-heading text-white mt-1">{formatConversionRate(analytics.conversionRate)}</p>
                        <p className="text-xs text-desti-text mt-1">{analytics.conversions || 0} ventes</p>
                      </div>
                      <ShoppingCart size={32} className="text-desti-gold opacity-50" />
                    </div>
                  </motion.div>
                </div>

                {/* Graphique Trafic dans le temps */}
                {analytics.trafficOverTime && analytics.trafficOverTime.length > 0 ? (
                  <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                    className="bg-desti-surface border border-white/5 p-6">
                    <h2 className="text-white font-medium mb-4">Trafic dans le temps</h2>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={analytics.trafficOverTime}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                          <XAxis dataKey="date" stroke="#888" fontSize={12} />
                          <YAxis stroke="#888" fontSize={12} />
                          <Tooltip
                            contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                            labelStyle={{ color: '#D4AF37' }}
                          />
                          <Area type="monotone" dataKey="direct" stackId="1" stroke="#D4AF37" fill="#D4AF37" fillOpacity={0.3} name="Direct" />
                          <Area type="monotone" dataKey="stripe" stackId="1" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.3} name="Stripe" />
                          <Area type="monotone" dataKey="fluent" stackId="1" stroke="#10B981" fill="#10B981" fillOpacity={0.3} name="Fluent" />
                          <Area type="monotone" dataKey="vercel" stackId="1" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.3} name="Vercel" />
                          <Legend />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </motion.div>
                ) : (
                  <div className="bg-desti-surface border border-white/5 p-6 text-center">
                    <p className="text-desti-text">Aucune donnée de trafic disponible</p>
                  </div>
                )}

                {/* Funnel de conversion et Sources de trafic */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
                    className="bg-desti-surface border border-white/5 p-6">
                    <h2 className="text-white font-medium mb-4">Funnel de conversion</h2>
                    {analytics.conversionFunnel && analytics.conversionFunnel.length > 0 ? (
                      <div className="space-y-3">
                        {analytics.conversionFunnel.map((step, idx) => (
                          <div key={step.name}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-desti-text">{step.name}</span>
                              <span className="text-white">{step.value}</span>
                            </div>
                            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-desti-gold rounded-full transition-all duration-500"
                                style={{ width: `${analytics.uniqueVisitors > 0 ? (step.value / analytics.uniqueVisitors) * 100 : 0}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-desti-text text-center py-8">Aucune donnée de funnel disponible</p>
                    )}
                  </motion.div>

                  <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                    className="bg-desti-surface border border-white/5 p-6">
                    <h2 className="text-white font-medium mb-4">Sources de trafic</h2>
                    {analytics.trafficSources && analytics.trafficSources.length > 0 ? (
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={analytics.trafficSources}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={80}
                              paddingAngle={5}
                              dataKey="value"
                              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                              labelLine={false}
                            >
                              {analytics.trafficSources.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <p className="text-desti-text text-center py-8">Aucune donnée de source disponible</p>
                    )}
                  </motion.div>
                </div>

                {/* Pages les plus vues */}
                <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
                  className="bg-desti-surface border border-white/5 p-6">
                  <h2 className="text-white font-medium mb-4">Pages les plus vues</h2>
                  {analytics.topPages && analytics.topPages.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="text-left py-3 text-desti-text font-medium text-sm">Page</th>
                            <th className="text-right py-3 text-desti-text font-medium text-sm">Vues</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analytics.topPages.map((page, idx) => (
                            <tr key={idx} className="border-b border-white/5">
                              <td className="py-3 text-white text-sm truncate max-w-md">{page.path}</td>
                              <td className="py-3 text-desti-text text-sm text-right">{page.views} vues</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-desti-text text-center py-8">Aucune donnée de pages disponible</p>
                  )}
                </motion.div>

                {/* Derniers événements */}
                <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                  className="bg-desti-surface border border-white/5 p-6">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <h2 className="text-white font-medium">Derniers événements</h2>
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse" />
                      <span className="text-xs text-desti-text">Auto-refresh toutes les 30s</span>
                      <span className="text-xs text-desti-text ml-2">Période : {periods.find(p => p.days === period)?.label || `${period} jours`}</span>
                    </div>
                  </div>
                  {analytics.recentEvents && analytics.recentEvents.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="text-left py-3 text-desti-text font-medium text-sm">Date</th>
                            <th className="text-left py-3 text-desti-text font-medium text-sm">Événement</th>
                            <th className="text-left py-3 text-desti-text font-medium text-sm">Page</th>
                            <th className="text-left py-3 text-desti-text font-medium text-sm">Source</th>
                            <th className="text-left py-3 text-desti-text font-medium text-sm">Pays</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analytics.recentEvents.map((event, idx) => (
                            <tr key={idx} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                              <td className="py-3 text-desti-text text-sm whitespace-nowrap">{event.date}</td>
                              <td className="py-3">
                                <span className={`text-xs px-2 py-1 rounded-full ${
                                  event.type === 'pageview' ? 'bg-blue-500/20 text-blue-400' :
                                  event.type === 'conversion' ? 'bg-green-500/20 text-green-400' :
                                  event.type === 'session_start' ? 'bg-purple-500/20 text-purple-400' :
                                  'bg-yellow-500/20 text-yellow-400'
                                }`}>
                                  {event.type}
                                </span>
                              </td>
                              <td className="py-3 text-white text-sm truncate max-w-[200px]">{event.page}</td>
                              <td className="py-3 text-desti-text text-sm">{event.source}</td>
                              <td className="py-3">
                                <div className="flex items-center gap-1">
                                  <MapPin size={12} className="text-desti-text" />
                                  <span className="text-desti-text text-sm">{event.country}</span>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-desti-text text-center py-8">Aucun événement récent disponible</p>
                  )}
                </motion.div>

                {/* Message quand aucune donnée */}
                {analytics.uniqueVisitors === 0 && !loading && !error && (
                  <div className="bg-desti-surface border border-white/5 p-8 text-center">
                    <Globe size={48} className="text-desti-gold opacity-50 mx-auto mb-4" />
                    <h3 className="text-white font-medium mb-2">Aucune donnée disponible</h3>
                    <p className="text-desti-text text-sm">
                      Les données d'analytics apparaîtront ici une fois que des utilisateurs auront visité votre site.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}